"use client";

import { useContext, forwardRef } from "react";
import styled from "styled-components";
import { sharedInputCss } from "../utils/sharedCss";
import { ValidityStateContext, IdContext, DescribedByContext } from "../utils/context";
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalUseOnlyTextArea = styled.textarea.withConfig({
  componentId: "sc-1wlqmm6-0"
})(["", ";overflow:auto;margin:0;height:8rem;"], sharedInputCss);

/**
 * The `TextArea` component is useful when the input expected from a user is more than 1 line of text.
 * @param {object} props - The component props
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the TextArea
 * @param {string} [props.defaultValue] - Default value for uncontrolled use
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {string} [props.maxLength] - The number of characters must not exceed the maxLength value
 * @param {string} [props.minLength] - The number of characters must not be less than the minLength value
 * @param {string} [props.name] - Used to reference the input in JavaScript, or to reference form data after a form is submitted
 * @param {Function} [props.onChange] - Callback to execute when the value changes
 * @param {boolean} [props.readOnly] - Makes the element not mutable
 * @param {boolean} [props.required] - Whether the input is required to be filled out to validate
 * @param {string} [props.value] - The value of the TextArea for controlled use
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The TextArea component
 */
const TextAreaComponent = ({
  "aria-describedby": ariaDescribedBy,
  id,
  ...props
}, ref) => {
  const validityStateContext = useContext(ValidityStateContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  return /*#__PURE__*/_jsx(InternalUseOnlyTextArea, {
    "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
    id: id ? id : idContext,
    ref: ref,
    $validityState: validityStateContext,
    ...props
  });
};
const TextArea = /*#__PURE__*/forwardRef(TextAreaComponent);
TextArea.displayName = "TextArea";

// Custom property for the FormField to consume
TextArea.isInput = true;
export default TextArea;